/**
 * Port of {@class ExpressionFieldRegistry} —
 * backend/src/main/java/com/irian/iam/ruleengine/api/ExpressionFieldRegistry.java
 *
 * Configurable schema for DSL expression fields. Defines the root entity, its
 * fields, nested scopes, and collection filter fields.
 */

import {FieldType, isCollection, isScope, isDynamic} from './FieldType'
import {parseFieldPath, endsWithCount as fieldPathEndsWithCount} from './FieldPath'

export type CollectionCountSpec = {
    readonly entityName: string
    readonly subjectField: string
    readonly typeField: string | null
    readonly validityWindow: boolean
}

export function collectionCountSpec(entityName: string, subjectField: string, typeField: string | null = null, validityWindow = true): CollectionCountSpec {
    return {entityName, subjectField, typeField, validityWindow}
}

export type FieldDefinition = {
    readonly name: string
    readonly valueType: FieldType
    readonly description: string
    readonly enumTypeName: string | null
    readonly countSpec: CollectionCountSpec | null
    /**
     * Issue 781 — only meaningful for ENUM fields. `true` when the underlying
     * type-table / value-list is semantically ordered; the CEL suggester widens
     * the operator pool to include `<`, `<=`, `>`, `>=` so authors can write
     * `riskLevel > 'MEDIUM'` (compared by sort_order at runtime). Unranked
     * enums keep equality + `in` only.
     */
    readonly ranked: boolean
    /**
     * Issue 936 — only meaningful for DYNAMIC fields. Names the AppConfig
     * key whose AttributeDefinition[] feeds the field-name provider. The
     * engine factory's {@link attachAllDynamicProviders} walks the schema
     * and wires the provider at the field's full dotted path — no
     * factory-side path constants needed.
     */
    readonly dynamicSource: string | null
}

export function fieldDefinition(name: string, valueType: FieldType, description: string, enumTypeName: string | null = null, countSpec: CollectionCountSpec | null = null): FieldDefinition {
    return {name, valueType, description, enumTypeName, countSpec, ranked: false, dynamicSource: null}
}

export function withDynamicSource(def: FieldDefinition, source: string): FieldDefinition {
    return {...def, dynamicSource: source}
}

export function withCountSpec(def: FieldDefinition, spec: CollectionCountSpec): FieldDefinition {
    return {...def, countSpec: spec}
}

export function withRanked(def: FieldDefinition, ranked: boolean): FieldDefinition {
    return {...def, ranked}
}

export function fieldDefinitionIsCollection(def: FieldDefinition): boolean {
    return isCollection(def.valueType)
}

export function fieldDefinitionIsScope(def: FieldDefinition): boolean {
    return isScope(def.valueType)
}

export function fieldDefinitionIsDynamic(def: FieldDefinition): boolean {
    return isDynamic(def.valueType)
}

export type RootDef = {
    readonly name: string
    readonly fields: ReadonlyMap<string, FieldDefinition>
    readonly scopeCode: string | null
}

export type ScopeDef = {
    readonly name: string
    readonly fields: ReadonlyMap<string, FieldDefinition>
}

export type CollectionDef = {
    readonly name: string
    readonly filterFields: ReadonlyMap<string, FieldDefinition>
    readonly scopeBinding: string | null
}

export type RootName = {readonly value: string}
export type ScopeName = {readonly value: string}
export type CollectionName = {readonly value: string}

export type PathClassification =
    | {readonly kind: 'Collection'; readonly name: CollectionName}
    | {readonly kind: 'Scope'; readonly name: ScopeName}
    | {readonly kind: 'None'}

export const PATH_NONE: PathClassification = {kind: 'None'}

export type ValueResult = {
    readonly values: readonly string[]
    readonly hasMore: boolean
}

export function valueResult(values: readonly string[], hasMore = false): ValueResult {
    return {values, hasMore}
}

export type ValueProvider = {
    getValues(prefix: string): ValueResult
}

export type FieldSuggestion = {
    readonly name: string
    readonly displayName: string
}

export type FieldNameProvider = {
    getFieldNames(prefix: string): readonly FieldSuggestion[]
}

/**
 * Build an ordered map from a list of field definitions. Insertion order is
 * preserved (JS Map maintains insertion order natively).
 */
export function orderedMap(fields: readonly FieldDefinition[]): ReadonlyMap<string, FieldDefinition> {
    const m = new Map<string, FieldDefinition>()
    for (const f of fields) m.set(f.name, f)
    return m
}

export class ExpressionFieldRegistry {
    private readonly rootEntityField: string
    private readonly roots: ReadonlyMap<string, RootDef>
    private readonly scopesMap: ReadonlyMap<string, ScopeDef>
    private readonly collectionsMap: ReadonlyMap<string, CollectionDef>
    private readonly valueProviders: ReadonlyMap<string, ValueProvider>
    private readonly fieldNameProviders: ReadonlyMap<string, FieldNameProvider>

    constructor(
        rootEntity: string,
        roots: ReadonlyMap<string, RootDef>,
        scopes: ReadonlyMap<string, ScopeDef>,
        collections: ReadonlyMap<string, CollectionDef>,
        valueProviders: ReadonlyMap<string, ValueProvider>,
        fieldNameProviders: ReadonlyMap<string, FieldNameProvider>,
    ) {
        this.rootEntityField = rootEntity
        this.roots = new Map(roots)
        this.scopesMap = new Map(scopes)
        this.collectionsMap = new Map(collections)
        this.valueProviders = new Map(valueProviders)
        this.fieldNameProviders = new Map(fieldNameProviders)
    }

    rootEntity(): string {
        return this.rootEntityField
    }

    scopeCodeForRoot(rootName: string): string | null {
        const def = this.roots.get(rootName)
        return def ? def.scopeCode : null
    }

    scopeCodeForCollection(collectionName: string): string | null {
        const def = this.collectionsMap.get(collectionName)
        return def ? def.scopeBinding : null
    }

    fieldNameProvider(path: string): FieldNameProvider | null {
        return this.fieldNameProviders.get(path) ?? null
    }

    valueProvider(entityContext: string | null, fieldName: string): ValueProvider | null {
        if (entityContext !== null) {
            const provider = this.valueProviders.get(`${entityContext}.${fieldName}`)
            if (provider) return provider
        }
        return this.valueProviders.get(fieldName) ?? null
    }

    rootFields(rootName?: string): ReadonlyMap<string, FieldDefinition> {
        const name = rootName ?? this.rootEntityField
        const def = this.roots.get(name)
        return def ? def.fields : new Map()
    }

    rootNames(): ReadonlySet<string> {
        return new Set(this.roots.keys())
    }

    containsRoot(rootName: string): boolean {
        return this.roots.has(rootName)
    }

    scopeFields(scopeName: string): ReadonlyMap<string, FieldDefinition> {
        const def = this.scopesMap.get(scopeName)
        return def ? def.fields : new Map()
    }

    collectionFilterFields(collectionName: string): ReadonlyMap<string, FieldDefinition> {
        const def = this.collectionsMap.get(collectionName)
        return def ? def.filterFields : new Map()
    }

    rootFieldNames(rootName?: string): ReadonlySet<string> {
        return new Set(this.rootFields(rootName).keys())
    }

    scopeFieldNames(scopeName: string): ReadonlySet<string> {
        return new Set(this.scopeFields(scopeName).keys())
    }

    collectionFilterFieldNames(collectionName: string): ReadonlySet<string> {
        return new Set(this.collectionFilterFields(collectionName).keys())
    }

    /**
     * Resolve the value type for a fully qualified field path. Keeps the
     * `.count` and bare-row short-circuits; delegates the structural walk to
     * `resolveFieldDefinition`. Mirrors Java's
     * `ExpressionFieldRegistry.resolveFieldType` (issue 829).
     */
    resolveFieldType(fieldPath: string | null): FieldType {
        if (fieldPath === null || fieldPath === undefined) return FieldType.STRING
        if (fieldPathEndsWithCount(parseFieldPath(fieldPath))) return FieldType.NUMBER
        if (fieldPath.endsWith(']') && fieldPath.includes('[')) return FieldType.DEFAULT
        const def = this.resolveFieldDefinition(fieldPath)
        return def ? def.valueType : FieldType.STRING
    }

    /**
     * Issue 781 — true when the resolved FieldDefinition is backed by a ranked
     * type-table / value-list. One-liner over `resolveFieldDefinition`.
     */
    resolveRanked(fieldPath: string | null): boolean {
        const def = this.resolveFieldDefinition(fieldPath)
        return def !== null && def.ranked
    }

    resolveEnumTypeName(fieldPath: string | null): string | null {
        const def = this.resolveFieldDefinition(fieldPath)
        return def ? def.enumTypeName : null
    }

    /**
     * Walk a dotted field path against the registry and return the matching
     * FieldDefinition, or `null` when the path doesn't resolve. Single source
     * of truth for `resolveEnumTypeName`, `resolveRanked`, and the leaf-type
     * lookup inside `resolveFieldType` (issue 829). Handles unqualified leaf
     * names, bare-collection `<collection>.<field>` paths (issue 781), and
     * arbitrary depth across root → scope → collection chains.
     */
    resolveFieldDefinition(fieldPath: string | null): FieldDefinition | null {
        if (fieldPath === null || fieldPath === undefined || fieldPath === '') return null
        const parts = fieldPath.split('.')
        if (parts.length === 1) return this.resolveUnqualifiedFieldDef(parts[0])
        if (this.collectionsMap.has(parts[0])) {
            return parts.length === 2 ? (this.collectionFilterFields(parts[0]).get(parts[1]) ?? null) : null
        }
        if (!this.containsRoot(parts[0])) return null
        let currentFields = this.rootFields(parts[0])
        let def: FieldDefinition | undefined
        for (let i = 1; i < parts.length; i++) {
            def = currentFields.get(parts[i])
            if (!def) return null
            if (i === parts.length - 1) return def
            if (fieldDefinitionIsScope(def)) {
                currentFields = this.scopeFields(def.name)
            } else if (fieldDefinitionIsCollection(def)) {
                currentFields = this.collectionFilterFields(def.name)
            } else {
                return null
            }
        }
        return def ?? null
    }

    private resolveUnqualifiedFieldDef(fieldName: string): FieldDefinition | null {
        for (const coll of this.collectionsMap.values()) {
            const def = coll.filterFields.get(fieldName)
            if (def) return def
        }
        for (const scope of this.scopesMap.values()) {
            const def = scope.fields.get(fieldName)
            if (def) return def
        }
        return null
    }

    resolveContainsFilterFields(fieldPath: string | null): ReadonlyMap<string, FieldDefinition> {
        if (fieldPath === null || fieldPath === undefined) return new Map()
        const parts = fieldPath.split('.')
        if (parts.length >= 2 && this.containsRoot(parts[0])) {
            return this.collectionFilterFields(parts[1])
        }
        return new Map()
    }

    classifyPath(fieldPath: string | null): PathClassification {
        const collection = this.deriveEntityContext(fieldPath)
        if (collection !== null) {
            return {kind: 'Collection', name: {value: collection}}
        }
        return PATH_NONE
    }

    deriveEntityContext(fieldPath: string | null): string | null {
        if (fieldPath === null || fieldPath === undefined) return null
        const unqualified = stripSegmentArgument(fieldPath)
        if (!unqualified.includes('.') && this.collectionsMap.has(unqualified)) {
            return unqualified
        }
        const parts = fieldPath.split('.')
        if (parts.length < 2 || !this.containsRoot(parts[0])) return null
        let currentFields = this.rootFields(parts[0])
        for (let i = 1; i < parts.length; i++) {
            const segment = stripSegmentArgument(parts[i])
            const def = currentFields.get(segment)
            if (!def) return null
            if (fieldDefinitionIsCollection(def)) {
                return this.collectionsMap.has(segment) ? segment : null
            }
            if (fieldDefinitionIsScope(def)) {
                currentFields = this.scopeFields(segment)
                continue
            }
            return null
        }
        return null
    }

}

function stripSegmentArgument(segment: string): string {
    const paren = segment.indexOf('(')
    return paren < 0 ? segment : segment.substring(0, paren)
}

// === Schema factories (mirrors of static factory methods on Java class) ===

function partyCollectionFields(): ReadonlyMap<string, FieldDefinition> {
    return orderedMap([
        fieldDefinition('type', FieldType.ENUM, 'Party type', 'party_types'),
        fieldDefinition('displayName', FieldType.STRING, 'Display name'),
        fieldDefinition('id', FieldType.STRING, 'Party UUID'),
        withDynamicSource(fieldDefinition('attributes', FieldType.DYNAMIC, 'Custom attributes'),
            'partyAttributeDefinitions'),
    ])
}

function accessProfileCollectionFields(): ReadonlyMap<string, FieldDefinition> {
    return orderedMap([
        fieldDefinition('code', FieldType.STRING, 'Access profile code'),
        fieldDefinition('name', FieldType.STRING, 'Access profile name'),
        fieldDefinition('id', FieldType.STRING, 'Access profile UUID'),
        fieldDefinition('riskLevel', FieldType.ENUM, 'Risk level', 'risk_level_types'),
    ])
}

function functionGroupBaseFields(): FieldDefinition[] {
    return [
        fieldDefinition('name', FieldType.STRING, 'Function group name'),
        fieldDefinition('type', FieldType.ENUM, 'Function group type', 'function_group_types'),
        fieldDefinition('id', FieldType.STRING, 'Function group UUID'),
        withDynamicSource(fieldDefinition('attributes', FieldType.DYNAMIC, 'Custom attributes'),
            'functionGroupAttributeDefinitions'),
    ]
}

export function relationshipFilterFields(): ReadonlyMap<string, FieldDefinition> {
    return orderedMap([
        fieldDefinition('type', FieldType.ENUM, 'Relationship type', 'party_relationship_types'),
        fieldDefinition('id', FieldType.STRING, 'Relationship UUID'),
    ])
}

function withHierarchyFields(base: ReadonlyMap<string, FieldDefinition>): ReadonlyMap<string, FieldDefinition> {
    const result = new Map(base)
    result.set('parents', fieldDefinition('parents', FieldType.COLLECTION, 'Parent hierarchy'))
    result.set('children', fieldDefinition('children', FieldType.COLLECTION, 'Child hierarchy'))
    result.set('incomingRelationships', withCountSpec(
        fieldDefinition('incomingRelationships', FieldType.COLLECTION, 'Incoming relationship rows'),
        collectionCountSpec('PartyRelationship', 'toParty.id', 'relationshipType', true),
    ))
    result.set('outgoingRelationships', withCountSpec(
        fieldDefinition('outgoingRelationships', FieldType.COLLECTION, 'Outgoing relationship rows'),
        collectionCountSpec('PartyRelationship', 'fromParty.id', 'relationshipType', true),
    ))
    return result
}

export class ExpressionFieldRegistryBuilder {
    private rootEntityField: string | null = null
    private rootFieldsMap: ReadonlyMap<string, FieldDefinition> = new Map()
    private readonly additionalRoots = new Map<string, ReadonlyMap<string, FieldDefinition>>()
    private readonly scopes = new Map<string, ReadonlyMap<string, FieldDefinition>>()
    private readonly collections = new Map<string, ReadonlyMap<string, FieldDefinition>>()
    private readonly valueProviders = new Map<string, ValueProvider>()
    private readonly fieldNameProviders = new Map<string, FieldNameProvider>()
    private readonly rootScopes = new Map<string, string>()
    private readonly collectionScopes = new Map<string, string>()
    private rankedEnumTypeNamesSet: ReadonlySet<string> = new Set()

    root(entityName: string, fields: ReadonlyMap<string, FieldDefinition>): this {
        this.rootEntityField = entityName
        this.rootFieldsMap = fields
        return this
    }

    additionalRoot(entityName: string, fields: ReadonlyMap<string, FieldDefinition>): this {
        this.additionalRoots.set(entityName, fields)
        return this
    }

    scope(scopeName: string, fields: ReadonlyMap<string, FieldDefinition>): this {
        this.scopes.set(scopeName, fields)
        return this
    }

    collection(collectionName: string, filterFields: ReadonlyMap<string, FieldDefinition>): this {
        this.collections.set(collectionName, filterFields)
        return this
    }

    valueProvider(key: string, provider: ValueProvider): this {
        this.valueProviders.set(key, provider)
        return this
    }

    fieldNameProvider(path: string, provider: FieldNameProvider): this {
        this.fieldNameProviders.set(path, provider)
        return this
    }

    rootScope(rootName: string, scopeCode: string): this {
        this.rootScopes.set(rootName, scopeCode)
        return this
    }

    collectionScope(collectionName: string, scopeCode: string): this {
        this.collectionScopes.set(collectionName, scopeCode)
        return this
    }

    /**
     * Issue 781 — set of `enumTypeName` values whose owning type-table /
     * value-list is semantically ordered. The builder folds the flag onto every
     * matching FieldDefinition so the suggester can read `def.ranked` without
     * an extra lookup. Mirrors the Java
     * {@code ExpressionFieldRegistry.Builder#rankedEnumTypeNames}.
     */
    rankedEnumTypeNames(names: ReadonlySet<string> | readonly string[]): this {
        this.rankedEnumTypeNamesSet = new Set(names)
        return this
    }

    build(): ExpressionFieldRegistry {
        if (this.rootEntityField === null || this.rootEntityField.trim() === '') {
            throw new Error('Root entity name is required')
        }
        const rootEntity = this.rootEntityField
        const applyRanked = (fields: ReadonlyMap<string, FieldDefinition>): ReadonlyMap<string, FieldDefinition> => {
            if (this.rankedEnumTypeNamesSet.size === 0) return fields
            const out = new Map<string, FieldDefinition>()
            for (const [k, def] of fields) {
                if (def.enumTypeName !== null && this.rankedEnumTypeNamesSet.has(def.enumTypeName)) {
                    out.set(k, withRanked(def, true))
                } else {
                    out.set(k, def)
                }
            }
            return out
        }
        const rootNodes = new Map<string, RootDef>()
        rootNodes.set(rootEntity, {name: rootEntity, fields: applyRanked(this.rootFieldsMap), scopeCode: this.rootScopes.get(rootEntity) ?? null})
        for (const [name, fields] of this.additionalRoots) {
            rootNodes.set(name, {name, fields: applyRanked(fields), scopeCode: this.rootScopes.get(name) ?? null})
        }
        const scopeNodes = new Map<string, ScopeDef>()
        for (const [name, fields] of this.scopes) {
            scopeNodes.set(name, {name, fields: applyRanked(fields)})
        }
        const collectionNodes = new Map<string, CollectionDef>()
        for (const [name, filterFields] of this.collections) {
            collectionNodes.set(name, {name, filterFields: applyRanked(filterFields), scopeBinding: this.collectionScopes.get(name) ?? null})
        }
        return new ExpressionFieldRegistry(rootEntity, rootNodes, scopeNodes, collectionNodes, this.valueProviders, this.fieldNameProviders)
    }
}

export function builder(): ExpressionFieldRegistryBuilder {
    return new ExpressionFieldRegistryBuilder()
}

export function profileSchema(): ExpressionFieldRegistry {
    return builder()
        .root('profile', orderedMap([
            fieldDefinition('code', FieldType.STRING, 'Access profile code'),
            fieldDefinition('name', FieldType.STRING, 'Access profile name'),
            fieldDefinition('riskLevel', FieldType.ENUM, 'Risk level', 'risk_level_types'),
            fieldDefinition('id', FieldType.STRING, 'Access profile UUID'),
        ]))
        .build()
}

export function iamSchemaBuilder(): ExpressionFieldRegistryBuilder {
    return builder()
        .root('identity', orderedMap([
            fieldDefinition('type', FieldType.ENUM, 'Identity type', 'identity_types'),
            fieldDefinition('lifecycleState', FieldType.ENUM, 'Lifecycle state', 'IdentityLifecycleState'),
            fieldDefinition('id', FieldType.STRING, 'Identity UUID'),
            withDynamicSource(fieldDefinition('attributes', FieldType.DYNAMIC, 'Custom attributes'),
                'identityAttributeDefinitions'),
            fieldDefinition('party', FieldType.SCOPE, 'Party fields'),
            withCountSpec(
                fieldDefinition('functionGroups', FieldType.COLLECTION, 'Function group collection'),
                collectionCountSpec('FunctionGroupAssignment', 'identity.id'),
            ),
            withCountSpec(
                fieldDefinition('assignedProfiles', FieldType.COLLECTION, 'Assigned access profiles'),
                collectionCountSpec('AccessProfileAssignment', 'identity.id'),
            ),
        ]))
        .scope('party', withHierarchyFields(partyCollectionFields()))
        .collection('party', partyCollectionFields())
        .collection('functionGroups', orderedMap(functionGroupBaseFields()))
        .collection('assignedProfiles', accessProfileCollectionFields())
        .collection('incomingRelationships', relationshipFilterFields())
        .collection('outgoingRelationships', relationshipFilterFields())
        .collection('parents', partyCollectionFields())
        .collection('children', partyCollectionFields())
}

export function iamSchema(): ExpressionFieldRegistry {
    return iamSchemaBuilder().build()
}

/**
 * Schema for certification-policy scope expressions (epic 924). Root entity is
 * `access` — the certified AccessProfileAssignment — with a strict scalar spine
 * `access → profile → resource → system` (nested singleton scopes, not
 * collections) plus a scalar `identity` + `identity.party` branch. Byte-identical
 * in shape to {@class ExpressionFieldRegistry}#certificationAccessSchema on the
 * Java side (spec §4.1). No collection, no dynamic-attribute, no row-id surface.
 * `riskLevel` is the only ranked enum.
 *
 * The target system is reached via the literal path
 * `access.profile.resource.system` — no collapsing alias.
 */
export function certificationAccessSchema(): ExpressionFieldRegistry {
    return certificationAccessSchemaBuilder().build()
}

export function certificationAccessSchemaBuilder(): ExpressionFieldRegistryBuilder {
    return builder()
        .root('access', orderedMap([
            fieldDefinition('startAt', FieldType.STRING, 'Grant start timestamp'),
            fieldDefinition('expiresAt', FieldType.STRING, 'Grant expiry timestamp'),
            fieldDefinition('lastCertifiedAt', FieldType.STRING, 'Last certification timestamp'),
            fieldDefinition('revokedAt', FieldType.STRING, 'Revocation timestamp'),
            fieldDefinition('status', FieldType.ENUM, 'Assignment status', 'certification_access_status'),
            fieldDefinition('isPermanent', FieldType.BOOLEAN, 'Permanent grant flag'),
            fieldDefinition('profile', FieldType.SCOPE, 'Access profile fields'),
            fieldDefinition('identity', FieldType.SCOPE, 'Holding identity fields'),
        ]))
        .scope('profile', orderedMap([
            fieldDefinition('code', FieldType.STRING, 'Access profile code'),
            fieldDefinition('name', FieldType.STRING, 'Access profile name'),
            fieldDefinition('riskLevel', FieldType.ENUM, 'Risk level', 'risk_level_types'),
            fieldDefinition('environment', FieldType.ENUM, 'Environment', 'environment_types'),
            fieldDefinition('status', FieldType.ENUM, 'Access profile status', 'AccessProfileStatus'),
            fieldDefinition('requestable', FieldType.BOOLEAN, 'Requestable flag'),
            fieldDefinition('autoAssignable', FieldType.BOOLEAN, 'Auto-assignable flag'),
            fieldDefinition('expiryRequired', FieldType.BOOLEAN, 'Expiry-required flag'),
            fieldDefinition('maxDurationDays', FieldType.NUMBER, 'Maximum grant duration in days'),
            fieldDefinition('reviewFrequencyDays', FieldType.NUMBER, 'Review frequency in days'),
            fieldDefinition('resource', FieldType.SCOPE, 'Resource fields'),
        ]))
        .scope('resource', orderedMap([
            fieldDefinition('resourceType', FieldType.ENUM, 'Resource type', 'resource_types'),
            fieldDefinition('dataClassification', FieldType.ENUM, 'Data classification', 'data_classification_types'),
            fieldDefinition('displayName', FieldType.STRING, 'Resource display name'),
            fieldDefinition('system', FieldType.SCOPE, 'Target system fields'),
            withDynamicSource(fieldDefinition('attributes', FieldType.DYNAMIC, 'Custom resource attributes'),
                'resourceAttributeDefinitions'),
        ]))
        .scope('system', orderedMap([
            fieldDefinition('systemType', FieldType.ENUM, 'System type', 'system_types'),
            fieldDefinition('environment', FieldType.ENUM, 'Environment', 'environment_types'),
            fieldDefinition('name', FieldType.STRING, 'Target system name'),
            fieldDefinition('description', FieldType.STRING, 'Target system description'),
            withDynamicSource(fieldDefinition('attributes', FieldType.DYNAMIC, 'Custom target system attributes'),
                'targetSystemAttributeDefinitions'),
        ]))
        .scope('identity', orderedMap([
            fieldDefinition('type', FieldType.ENUM, 'Identity type', 'identity_types'),
            fieldDefinition('lifecycleState', FieldType.ENUM, 'Lifecycle state', 'IdentityLifecycleState'),
            fieldDefinition('party', FieldType.SCOPE, 'Party fields'),
            withDynamicSource(fieldDefinition('attributes', FieldType.DYNAMIC, 'Custom identity attributes'),
                'identityAttributeDefinitions'),
        ]))
        .scope('party', orderedMap([
            fieldDefinition('displayName', FieldType.STRING, 'Display name'),
            fieldDefinition('type', FieldType.ENUM, 'Party type', 'party_types'),
            withDynamicSource(fieldDefinition('attributes', FieldType.DYNAMIC, 'Custom party attributes'),
                'partyAttributeDefinitions'),
        ]))
}

export function iamMultiRootSchemaBuilder(): ExpressionFieldRegistryBuilder {
    const partyTopLevelFields = withHierarchyFields(partyCollectionFields())
    const functionGroupTopLevelFields = orderedMap([
        ...functionGroupBaseFields(),
        fieldDefinition('ownerParty', FieldType.SCOPE, 'Owner party fields'),
        fieldDefinition('incomingRelationships', FieldType.COLLECTION, 'Incoming relationship rows'),
        fieldDefinition('outgoingRelationships', FieldType.COLLECTION, 'Outgoing relationship rows'),
    ])
    const accessProfileTopLevelFields = accessProfileCollectionFields()
    const attributeDefinitionFields = orderedMap([
        fieldDefinition('id', FieldType.STRING, 'Attribute definition UUID'),
        fieldDefinition('name', FieldType.STRING, 'Attribute name'),
        fieldDefinition('displayName', FieldType.STRING, 'Display name'),
        fieldDefinition('valueType', FieldType.ENUM, 'Value type', 'AttributeValueType'),
        fieldDefinition('multivalued', FieldType.BOOLEAN, 'Multi-valued attribute'),
        fieldDefinition('maxOccurrences', FieldType.NUMBER, 'Maximum number of values'),
        fieldDefinition('allowedValuesSource', FieldType.ENUM, 'Allowed values source', 'ValueSourceType'),
        fieldDefinition('description', FieldType.STRING, 'Description'),
        fieldDefinition('entityType', FieldType.ENUM, 'Owning entity type', 'EntityType'),
    ])
    const restrictionRuleTopLevelFields = orderedMap([
        fieldDefinition('id', FieldType.STRING, 'Restriction rule UUID'),
        fieldDefinition('name', FieldType.STRING, 'Restriction rule name'),
        fieldDefinition('isActive', FieldType.BOOLEAN, 'Active flag'),
        fieldDefinition('ownerHasApproverPath', FieldType.BOOLEAN,
            'True when the owner party has at least one ACTIVE direct identity or an incoming MEMBER_OF relationship from a party with at least one ACTIVE identity'),
    ])
    return iamSchemaBuilder()
        .additionalRoot('party', partyTopLevelFields)
        .additionalRoot('functionGroup', functionGroupTopLevelFields)
        .additionalRoot('accessProfile', accessProfileTopLevelFields)
        .additionalRoot('partyAttributeDefinition', attributeDefinitionFields)
        .additionalRoot('identityAttributeDefinition', attributeDefinitionFields)
        .additionalRoot('restrictionRule', restrictionRuleTopLevelFields)
        .scope('ownerParty', withHierarchyFields(orderedMap([
            fieldDefinition('type', FieldType.ENUM, 'Party type', 'party_types'),
            fieldDefinition('displayName', FieldType.STRING, 'Display name'),
            fieldDefinition('externalId', FieldType.STRING, 'External business key'),
            fieldDefinition('id', FieldType.STRING, 'Party UUID'),
            withDynamicSource(fieldDefinition('attributes', FieldType.DYNAMIC, 'Custom attributes'),
                'partyAttributeDefinitions'),
        ])))
}
